import React, { useState, useMemo } from 'react';

import { Checkbox } from 'antd';
import _ from 'lodash';

import { getFileTypeIcons } from '@libs/NwUtils';

import CustomSwitch from '@components/Gui/CustomSwitch';
import { NwAddButton, NwButton, NwLinkButton } from '@components/Gui/NwButton';
import NwDivider from '@components/Gui/NwDivider';
import NwDrawer from '@components/Gui/NwDrawer';
import NwIcon from '@components/Gui/NwIcon';
import { ModelCover } from '@components/ModelCover';
import SendDrawer from '@components/SendDrawer/SendDrawer';

import ProjectEmailPreview from './ProjectEmailPreview';
import { EmailItem } from './SendProjectDrawerUI';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const EmailToSendItem = ({
    message,
    signature,
    onAddEmail,
    onRemoveEmail,
    onSelect,
    onSelectModelDocument,
    recipient,
}) => {
    const [showPreview, setShowPreview] = useState(false);
    const [showAddressAdd, setShowAddressAdd] = useState(false);

    const handleAddEmail = (email) => {
        onAddEmail(recipient.ModelData.ID, { email: email.Email, ccs: email.CC });
        setShowAddressAdd(false);
    };

    const existingEmails = useMemo(() => {
        if (recipient.Emails.length === 0) {
            return [];
        }
        return recipient.Emails.map((email) => ({ Email: email.email.toLowerCase() }));
    }, [recipient.Emails]);

    return (
        <>
            <EmailItem>
                <div className="checkbox">
                    <CustomSwitch
                        checked={recipient.Selected && recipient.Emails.length > 0}
                        disabled={recipient.Emails.length === 0}
                        onChange={(check) => onSelect(recipient.ModelData.ID, check)}
                        checkedChildren={<NwIcon icon={byPrefixAndName.fasl['check']} />}
                        unCheckedChildren={<NwIcon icon={byPrefixAndName.fasl['times']} />}
                    />
                </div>
                <div className="model-cover">
                    <ModelCover maxWidth={80} model={recipient.ModelData} classN="model-cover-small" />
                </div>
                <div className="model-details">
                    <h2>{recipient.ModelData.Name.toLowerCase()}</h2>
                    <small>
                        {recipient.DocVersion.EventDatesModels.length} booking events,{' '}
                        {recipient.DocVersion.Travels.length} travels, {recipient.DocVersion.FollowUps.length} other
                        activities
                    </small>
                    <div className="mail-addresses-container">
                        {recipient.Emails.length === 0 && (
                            <div className="mail-address warning">
                                <div className="icon-container">
                                    <NwIcon icon={byPrefixAndName.fasl['triangle-exclamation']} />
                                </div>
                                <p>no e-mail address</p>
                            </div>
                        )}
                        {recipient.Emails.map((email, index) => (
                            <div className="mail-address" key={index}>
                                <div className="icon-container">
                                    <NwIcon icon={byPrefixAndName.fasl['envelope']} />
                                </div>
                                <p>
                                    {email.email}
                                    {email.ccs && email.ccs.length > 0 && (
                                        <span className="ccs">(cc: {email.ccs.join(', ')})</span>
                                    )}
                                </p>
                                <NwButton
                                    iconOnly
                                    minimal
                                    icon={byPrefixAndName.fass['circle-xmark']}
                                    onClick={() => onRemoveEmail(recipient.ModelData.ID, email.email)}
                                />
                            </div>
                        ))}
                    </div>
                    <p>
                        <NwAddButton ghost onClick={() => setShowAddressAdd(true)} label="add address" />
                    </p>
                    {recipient.Attachments && recipient.Attachments.length > 0 && (
                        <div className="model-documents-container">
                            <NwDivider>Model Attachments</NwDivider>
                            {recipient.Attachments.map((modeldocument) => (
                                <div className="model-document" key={modeldocument.DocumentID}>
                                    <Checkbox
                                        checked={modeldocument.Selected}
                                        onChange={(e) =>
                                            onSelectModelDocument(recipient.ModelData.ID, modeldocument.DocumentID, e)
                                        }
                                    >
                                        <NwIcon icon={getFileTypeIcons(modeldocument.DocumentData.Extension)} />
                                        {modeldocument.DocumentData.Title}
                                    </Checkbox>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className="model-actions">
                    <div className="icon-container">
                        <NwIcon
                            primary
                            pointer
                            icon={byPrefixAndName.fasl['envelope-open-text']}
                            onClick={() => setShowPreview(true)}
                        />
                    </div>
                    <NwLinkButton onClick={() => setShowPreview(true)} label="preview e-mail" />
                </div>
            </EmailItem>
            {showPreview && (
                <ProjectEmailPreview
                    doc={recipient.DocVersion}
                    message={message}
                    signature={signature}
                    onClose={() => setShowPreview(false)}
                />
            )}
            {showAddressAdd && (
                <NwDrawer padding="0px" title="Add Recipients" onClose={() => setShowAddressAdd(false)}>
                    <SendDrawer
                        existingEmails={existingEmails}
                        searchBox
                        onSelectRecipient={handleAddEmail}
                        newContact={false}
                        onCancel={() => setShowAddressAdd(false)}
                    />
                </NwDrawer>
            )}
        </>
    );
};

export default EmailToSendItem;
